import React from "react";
import { Box, Paper, Skeleton, Divider } from "@mui/material";
import { makeStyles } from "@mui/styles";

const useStyles = makeStyles((theme: any) => ({
  mainContainer: {
    width: "100%",
    padding: theme.spacing(3),
    backgroundColor: "#ffffff",
    minHeight: "100vh",
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  paperContainer: {
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    borderRadius: theme.spacing(1),
    backgroundColor: "#ffffff",
    border: "1px solid #e0e0e0",
  },
  sectionTitleSkeleton: {
    marginBottom: theme.spacing(2),
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: theme.spacing(1),
  },
  gridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
    gap: theme.spacing(2),
  },
  fieldSkeleton: {
    height: 56,
    borderRadius: theme.spacing(0.5),
  },
  switchGridContainer: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  switchSkeleton: {
    height: 48,
    borderRadius: theme.spacing(1),
  },
  simpleDivider: {
    margin: `${theme.spacing(3)}px 0`,
  },
  tableSkeleton: {
    marginTop: theme.spacing(2),
  },
  tableRowSkeleton: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    padding: theme.spacing(1),
    backgroundColor: "#f5f5f5",
    borderRadius: theme.spacing(0.5),
  },
}));

const OperatorEditorSkeleton = () => {
  const styles = useStyles();

  return (
    <Box className={styles.mainContainer}>
      {/* Operator Information Section Skeleton */}
      <Paper elevation={1} className={styles.paperContainer}>
        <Box className={styles.sectionTitleSkeleton}>
          <Skeleton variant="text" width={280} height={32} />
        </Box>
        <Box className={styles.gridContainer}>
          {[1, 2, 3, 4, 5].map((item) => (
            <Box key={item}>
              <Skeleton variant="text" width={100} height={20} sx={{ mb: 1 }} />
              <Skeleton
                variant="rectangular"
                className={styles.fieldSkeleton}
                animation="wave"
              />
            </Box>
          ))}
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Skeleton
            variant="rectangular"
            width={150}
            height={40}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      </Paper>

      {/* Features & Settings Section Skeleton */}
      <Paper elevation={1} className={styles.paperContainer}>
        <Box className={styles.sectionTitleSkeleton}>
          <Skeleton variant="text" width={200} height={32} />
        </Box>
        <Box className={styles.switchGridContainer}>
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Box key={item}>
              <Skeleton variant="text" width={150} height={20} sx={{ mb: 1 }} />
              <Skeleton
                variant="rectangular"
                className={styles.switchSkeleton}
                animation="wave"
              />
            </Box>
          ))}
        </Box>
        <Box display="flex" justifyContent="flex-end" mt={2}>
          <Skeleton
            variant="rectangular"
            width={150}
            height={40}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      </Paper>

      {/* Vault Configuration Section Skeleton */}
      <Paper elevation={1} className={styles.paperContainer}>
        <Box className={styles.sectionTitleSkeleton}>
          <Skeleton variant="text" width={180} height={32} />
          <Skeleton variant="text" width={320} height={20} sx={{ mt: 1 }} />
        </Box>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 3,
            mb: 2,
          }}
        >
          {[1, 2].map((item) => (
            <Box key={item}>
              <Skeleton variant="text" width={80} height={20} sx={{ mb: 1 }} />
              <Skeleton
                variant="rectangular"
                className={styles.fieldSkeleton}
                animation="wave"
              />
            </Box>
          ))}
        </Box>
        <Box display="flex" justifyContent="flex-end">
          <Skeleton
            variant="rectangular"
            width={160}
            height={40}
            sx={{ borderRadius: 1 }}
          />
        </Box>
      </Paper>

      <Divider className={styles.simpleDivider} />

      {/* Margin Lock Toggle Skeleton */}
      <Paper elevation={1} className={styles.paperContainer}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box>
            <Skeleton variant="text" width={200} height={28} />
            <Skeleton variant="text" width={350} height={20} sx={{ mt: 0.5 }} />
          </Box>
          <Skeleton
            variant="rectangular"
            width={60}
            height={34}
            sx={{ borderRadius: 4 }}
          />
        </Box>
      </Paper>

      {/* Bet Limits Section Skeleton */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: "2px dashed #ff9800",
          backgroundColor: "rgba(255, 152, 0, 0.02)",
        }}
      >
        <Box mb={2}>
          <Skeleton variant="text" width={140} height={28} />
          <Skeleton variant="text" width={280} height={20} sx={{ mt: 0.5 }} />
        </Box>
        <Box display="flex" gap={2} mb={3}>
          <Skeleton
            variant="rectangular"
            width={130}
            height={40}
            sx={{ borderRadius: 1 }}
          />
          <Skeleton
            variant="rectangular"
            width={110}
            height={40}
            sx={{ borderRadius: 1 }}
          />
        </Box>
        {/* Bet Limit Items */}
        {[1, 2].map((item) => (
          <Box key={item} className={styles.tableRowSkeleton}>
            <Skeleton variant="rectangular" width="25%" height={40} />
            <Skeleton variant="rectangular" width="20%" height={40} />
            <Skeleton variant="rectangular" width="20%" height={40} />
            <Skeleton variant="rectangular" width="20%" height={40} />
            <Skeleton variant="circular" width={40} height={40} />
          </Box>
        ))}
      </Paper>

      {/* Margin & RTP Section Skeleton */}
      <Paper
        elevation={1}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: "2px dashed #ff9800",
          backgroundColor: "rgba(255, 152, 0, 0.02)",
        }}
      >
        <Box mb={3}>
          <Skeleton variant="text" width={260} height={28} />
          <Skeleton variant="text" width={400} height={20} sx={{ mt: 0.5 }} />
        </Box>

        {/* Overall RTP Box */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 3,
            backgroundColor: "#f8f9fa",
            borderRadius: 1,
            mb: 3,
            border: "1px solid #e9ecef",
          }}
        >
          <Box>
            <Skeleton variant="text" width={180} height={28} />
            <Skeleton variant="text" width={260} height={20} sx={{ mt: 0.5 }} />
          </Box>
          <Box textAlign="right">
            <Skeleton variant="text" width={80} height={20} />
            <Skeleton variant="text" width={100} height={40} sx={{ mt: 0.5 }} />
          </Box>
        </Box>

        {/* Table Skeleton */}
        <Box className={styles.tableSkeleton}>
          {/* Table Header */}
          <Box
            sx={{
              display: "flex",
              gap: 2,
              p: 1.5,
              backgroundColor: "#f5f5f5",
              borderRadius: 1,
              mb: 1,
            }}
          >
            <Skeleton variant="text" width="25%" height={24} />
            <Skeleton variant="text" width="15%" height={24} />
            <Skeleton variant="text" width="20%" height={24} />
            <Skeleton variant="text" width="15%" height={24} />
            <Skeleton variant="text" width="25%" height={24} />
          </Box>
          {/* Table Rows */}
          {[1, 2, 3, 4, 5, 6].map((item) => (
            <Box
              key={item}
              sx={{
                display: "flex",
                gap: 2,
                p: 1.5,
                borderBottom: "1px solid #e0e0e0",
                alignItems: "center",
              }}
            >
              <Skeleton
                variant="text"
                width="25%"
                height={24}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width="15%"
                height={24}
                animation="wave"
              />
              <Skeleton
                variant="rectangular"
                width="20%"
                height={40}
                animation="wave"
              />
              <Skeleton
                variant="text"
                width="15%"
                height={24}
                animation="wave"
              />
              <Skeleton
                variant="rectangular"
                width="25%"
                height={24}
                animation="wave"
              />
            </Box>
          ))}
        </Box>
      </Paper>
    </Box>
  );
};

export default OperatorEditorSkeleton;
